// src/components/admin/RoleManagement.jsx
// Complete Role Management System - FLAT ICONS ONLY

import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { StatusBadge } from '../ui/StatusBadge';
import { useAuth } from '../../contexts/AuthContext';
import { fetchRoles, fetchAuthRoles } from '../../utils/api';

const FALLBACK_ROLES = {
  'super-user': {
    id: 'super-user',
    name: 'Super User',
    iconText: 'SU',
    color: '#2C5FA8',
    description: 'Operational architect / power user',
    purpose: 'Translate organizational strategy into executable workflows and standards inside the platform.',
    capabilities: [
      'Configure dashboards, templates, and task frameworks',
      'Define standardized task checklists (daily, weekly, monthly, quarterly)',
      'Set thresholds, alerts, and escalation rules',
      'Curate quality, access, staffing, and finance metrics',
      'Manage training modules and operational controls',
      'Enable or disable features at the market or service line level',
      'Serve as the bridge between operations, IT, and leadership'
    ],
    careLevels: ['Consistency across markets', 'Scalability', 'Reducing variability', 'Making the system usable, not just powerful'],
    permissions: {
      dashboards: { configure: true, read: true, update: true },
      workflows: { create: true, read: true, update: true, delete: true },
      metrics: { configure: true, read: true, update: true },
      alerts: { create: true, read: true, update: true, delete: true },
      features: { toggle: true, read: true },
      training: { create: true, read: true, update: true, delete: true },
      data: { marketLevel: true }
    }
  },
  'director': {
    id: 'director',
    name: 'Director / VP',
    iconText: 'DR',
    color: '#00CED1',
    description: 'Strategic oversight and accountability layer',
    purpose: 'Maintain real-time visibility across multiple units and ensure execution aligns with organizational goals.',
    capabilities: [
      'View roll-up dashboards across regions, MOBs, and service lines',
      'Drill down from enterprise → region → clinic',
      'Monitor trends for Access, Quality, Staffing, Finance, Initiatives, Consumer Insights',
      'Identify risk areas and performance outliers',
      'Assign priorities and deadlines to managers',
      'Track initiative ownership and completion',
      'Monitor training deployment and compliance',
      'Receive alerts for threshold breaches or stalled initiatives'
    ],
    careLevels: ['Early signal detection', 'Accountability', 'Consistent execution', 'Fewer surprises'],
    permissions: {
      dashboards: { read: true, drillDown: true },
      metrics: { read: true, allPillars: true },
      initiatives: { assign: true, track: true, read: true },
      alerts: { read: true, acknowledge: true },
      reports: { generate: true, export: true },
      team: { viewHierarchy: true, assignTasks: true },
      data: { serviceLineLevel: true }
    }
  },
  'manager': {
    id: 'manager',
    name: 'Manager',
    iconText: 'MG',
    color: '#10B981',
    description: 'Execution and frontline operations layer',
    purpose: 'Execute daily operations with clarity, prioritization, and reduced cognitive load.',
    capabilities: [
      'Personalized task and priority queue',
      'View clinic-level performance metrics',
      'Complete and track operational checklists',
      'Manage staffing gaps and urgent issues',
      'Receive alerts and deadlines from leadership',
      'Document actions taken and resolutions',
      'Surface issues upward with context',
      'Track required training and certifications'
    ],
    careLevels: ['Knowing what matters today', 'Clear priorities', 'Less mental clutter', 'Confidence nothing is being missed'],
    permissions: {
      dashboards: { read: true, clinicLevel: true },
      queue: { manage: true, addItems: true, complete: true },
      checklists: { complete: true, document: true },
      metrics: { read: true, clinicOnly: true },
      alerts: { read: true, respond: true },
      initiatives: { submit: true, update: true },
      training: { complete: true, track: true },
      data: { clinicLevel: true }
    }
  }
};

// Flat Icon Component
function FlatIcon({ text, color, size = 32 }) {
  return (
    <div style={{
      width: size,
      height: size,
      borderRadius: '6px',
      background: color,
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: size * 0.4,
      fontWeight: '600',
      letterSpacing: '-0.5px'
    }}>
      {text}
    </div>
  );
}

function RoleManagement() {
  const { getToken } = useAuth();
  const [activeView, setActiveView] = useState('overview');
  const [selectedRole, setSelectedRole] = useState(null);
  const [roles, setRoles] = useState(FALLBACK_ROLES);
  const [users, setUsers] = useState([
    { id: 1, name: 'John Smith', email: 'john.smith@kaiser.com', role: 'super-user', status: 'active', market: 'All Markets', lastLogin: '2 hours ago' },
    { id: 2, name: 'Sarah Chen', email: 'sarah.chen@kaiser.com', role: 'manager', status: 'active', market: 'West Region - Primary Care', lastLogin: 'Active now' },
    { id: 3, name: 'Michael Torres', email: 'michael.torres@kaiser.com', role: 'director', status: 'active', market: 'North Region', lastLogin: '1 hour ago' },
    { id: 4, name: 'Emily Rodriguez', email: 'emily.rodriguez@kaiser.com', role: 'super-user', status: 'active', market: 'All Markets', lastLogin: '30 min ago' },
    { id: 5, name: 'David Kim', email: 'david.kim@kaiser.com', role: 'manager', status: 'active', market: 'South Region - Specialty', lastLogin: '15 min ago' }
  ]);

  useEffect(() => {
    // Try auth roles endpoint first (system-managed, read-only), fall back to legacy roles endpoint
    fetchAuthRoles(getToken)
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          const mapped = {};
          for (const r of data) mapped[r.id] = r;
          setRoles(mapped);
        } else {
          return fetchRoles(getToken);
        }
      })
      .then((data) => {
        if (!data) return;
        if (data && typeof data === 'object' && !Array.isArray(data)) {
          setRoles(data);
        } else if (Array.isArray(data) && data.length > 0) {
          const mapped = {};
          for (const r of data) mapped[r.id] = r;
          setRoles(mapped);
        }
      })
      .catch(() => {
        // Keep fallback roles on error
      });
  }, [getToken]);

  const ROLES = roles;

  return (
    <div className="dashboard-container">
      {/* Header */}
      <div className="dashboard-header">
        <h1 className="dashboard-title">Role Management</h1>
        <p className="dashboard-subtitle">Configure roles, permissions, and user access</p>
      </div>

      {/* View Navigation */}
      <nav className="flex gap-1 border-b border-nn-border mb-4 overflow-x-auto">
        {[
          { id: 'overview', label: 'Overview' },
          { id: 'roles', label: 'Role Definitions' },
          { id: 'users', label: 'User Management' },
          { id: 'permissions', label: 'Permissions Matrix' }
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveView(tab.id)}
            className={`tab${activeView === tab.id ? ' active' : ''}`}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {/* Content */}
      <div style={{ marginTop: '24px' }}>
        {activeView === 'overview' && <OverviewView roles={ROLES} users={users} />}
        {activeView === 'roles' && <RoleDefinitionsView roles={ROLES} onSelectRole={setSelectedRole} />}
        {activeView === 'users' && <UserManagementView roles={ROLES} users={users} setUsers={setUsers} />}
        {activeView === 'permissions' && <PermissionsMatrixView roles={ROLES} />}
      </div>

      {/* Role Detail Modal */}
      {selectedRole && (
        <RoleDetailModal
          role={ROLES[selectedRole]}
          onClose={() => setSelectedRole(null)}
        />
      )}
    </div>
  );
}

// Overview View
function OverviewView({ roles, users }) {
  const roleStats = Object.keys(roles).map(roleId => ({
    ...roles[roleId],
    userCount: users.filter(u => u.role === roleId).length
  }));

  return (
    <div>
      {/* Role Distribution */}
      <div className="section-header">
        <h2 className="section-title">Role Distribution</h2>
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-5">
        {roleStats.map(role => (
          <div key={role.id} className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative border-l-4 border-l-aqua" style={{ borderLeftColor: role.color }}>
            <div className="p-5">
              <div style={{ marginBottom: '12px' }}>
                <FlatIcon text={role.iconText} color={role.color} size={40} />
              </div>
              <div className="text-[13px] text-text-dark-secondary font-normal mb-2 block">{role.name}</div>
              <div className="text-[34px] font-bold text-text-dark tracking-tight mb-1.5 block">{role.userCount}</div>
              <div className="flex items-center gap-1 text-[13px] mb-4 text-text-dark-secondary">Active users</div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Stats */}
      <div className="section-header" style={{ marginTop: '40px' }}>
        <h3 className="section-title">System Overview</h3>
      </div>

      <div className="grid grid-cols-3 gap-5">
        <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative border-l-4 border-l-green-nn">
          <div className="p-5">
            <div className="text-[13px] text-text-dark-secondary font-normal mb-2 block">Total Users</div>
            <div className="text-[34px] font-bold text-text-dark tracking-tight mb-1.5 block">{users.length}</div>
            <div className="flex items-center gap-1 text-[13px] mb-4 text-green-nn">All active</div>
          </div>
        </div>

        <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative border-l-4 border-l-aqua">
          <div className="p-5">
            <div className="text-[13px] text-text-dark-secondary font-normal mb-2 block">Active Sessions</div>
            <div className="text-[34px] font-bold text-text-dark tracking-tight mb-1.5 block">{users.filter(u => u.lastLogin.includes('min') || u.lastLogin.includes('now')).length}</div>
            <div className="flex items-center gap-1 text-[13px] mb-4 text-text-dark-secondary">Last 30 minutes</div>
          </div>
        </div>

        <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative border-l-4 border-l-royal">
          <div className="p-5">
            <div className="text-[13px] text-text-dark-secondary font-normal mb-2 block">Markets Configured</div>
            <div className="text-[34px] font-bold text-text-dark tracking-tight mb-1.5 block">8/10</div>
            <div className="flex items-center gap-1 text-[13px] mb-4 text-text-dark-secondary">2 pending setup</div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="section-header" style={{ marginTop: '40px' }}>
        <h3 className="section-title">Recent Activity</h3>
      </div>

      <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative">
        <div className="p-5">
          <div style={{ fontSize: '13px', color: '#6B7280', lineHeight: '2' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #F3F4F6' }}>
              <span><strong>Emily Rodriguez</strong> configured new alert rules for West Region</span>
              <span style={{ color: '#9CA3AF' }}>30 min ago</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #F3F4F6' }}>
              <span><strong>John Smith</strong> created 3 new user accounts</span>
              <span style={{ color: '#9CA3AF' }}>2 hours ago</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #F3F4F6' }}>
              <span><strong>Michael Torres</strong> assigned 5 new initiatives to managers</span>
              <span style={{ color: '#9CA3AF' }}>3 hours ago</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
              <span><strong>Emily Rodriguez</strong> updated task checklist templates</span>
              <span style={{ color: '#9CA3AF' }}>Yesterday</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Role Definitions View
function RoleDefinitionsView({ roles, onSelectRole }) {
  return (
    <div>
      <div className="section-header">
        <h2 className="section-title">Role Definitions</h2>
        <p className="section-subtitle">View and configure role capabilities</p>
      </div>

      <div className="grid grid-cols-2 gap-5">
        {Object.values(roles).map(role => (
          <div key={role.id} className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative" style={{ cursor: 'pointer' }} onClick={() => onSelectRole(role.id)}>
            <div className="px-5 py-4 flex justify-between items-center" style={{ background: role.color }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <FlatIcon text={role.iconText} color="rgba(255,255,255,0.3)" size={40} />
                <div className="font-heading text-[14px] font-semibold text-text-dark" style={{ color: 'white' }}>{role.name}</div>
              </div>
            </div>
            <div className="p-5">
              <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px', color: '#1a1a1a' }}>
                {role.description}
              </div>
              <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '16px', lineHeight: '1.6' }}>
                {role.purpose}
              </div>

              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280', marginBottom: '8px', textTransform: 'uppercase' }}>
                  Core Capabilities ({role.capabilities.length})
                </div>
                <div style={{ fontSize: '12px', color: '#6B7280', lineHeight: '1.8' }}>
                  {role.capabilities.slice(0, 3).map((cap, idx) => (
                    <div key={idx}>• {cap}</div>
                  ))}
                  {role.capabilities.length > 3 && (
                    <div style={{ fontStyle: 'italic', marginTop: '4px' }}>
                      +{role.capabilities.length - 3} more...
                    </div>
                  )}
                </div>
              </div>

              <div>
                <div style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280', marginBottom: '8px', textTransform: 'uppercase' }}>
                  What They Care About
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {role.careLevels.map((care, idx) => (
                    <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-surface-warm text-text-dark-secondary border border-nn-border">{care}</span>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-nn-border">
                <Button variant="default" size="sm">View Details</Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// User Management View
function UserManagementView({ roles, users, setUsers }) {
  const [showAddUser, setShowAddUser] = useState(false);

  return (
    <div>
      <div className="section-header">
        <h2 className="section-title">User Management</h2>
        <Button variant="primary" size="sm" type="button" onClick={() => setShowAddUser(true)}>
          + Add User
        </Button>
      </div>

      <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative">
        <div style={{ padding: 0 }}>
          <table className="nn-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Market/Region</th>
                <th style={{ textAlign: 'center' }}>Status</th>
                <th style={{ textAlign: 'center' }}>Last Login</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => {
                const role = roles[user.role];
                if (!role) return null;
                return (
                  <tr key={user.id}>
                    <td>
                      <div style={{ fontWeight: '500', marginBottom: '2px' }}>{user.name}</div>
                      <div className="text-body-sm text-text-muted">{user.email}</div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FlatIcon text={role.iconText} color={role.color} size={24} />
                        <span style={{ fontWeight: '500' }}>{role.name}</span>
                      </div>
                    </td>
                    <td className="text-text-muted">
                      {user.market}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {user.status === 'active'
                        ? <StatusBadge variant="on-track">{user.status}</StatusBadge>
                        : <StatusBadge variant="info">{user.status}</StatusBadge>
                      }
                    </td>
                    <td className="text-text-muted" style={{ textAlign: 'center' }}>
                      {user.lastLogin}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <Button type="button" variant="default" size="sm" style={{ flex: 'none', marginRight: '6px' }}>
                        Edit
                      </Button>
                      <Button type="button" variant="danger" size="sm" style={{ flex: 'none' }}>
                        Deactivate
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showAddUser && (
        <AddUserModal roles={roles} onClose={() => setShowAddUser(false)} onAdd={(newUser) => {
          setUsers([...users, { ...newUser, id: Math.max(...users.map(u => u.id), 0) + 1, status: 'active', lastLogin: 'Never' }]);
          setShowAddUser(false);
        }} />
      )}
    </div>
  );
}

// Permissions Matrix View
function PermissionsMatrixView({ roles }) {
  const permissions = [
    { category: 'User Management', items: ['Create Users', 'Edit Users', 'Delete Users', 'Assign Roles'] },
    { category: 'Dashboard Access', items: ['View Own Dashboard', 'View Team Dashboard', 'Drill Down', 'Configure Dashboards'] },
    { category: 'Data Access', items: ['Clinic Level', 'Service Line Level', 'Market Level', 'Enterprise Level'] },
    { category: 'Workflows', items: ['View Tasks', 'Complete Tasks', 'Assign Tasks', 'Create Templates'] },
    { category: 'Metrics & Alerts', items: ['View Metrics', 'Configure Thresholds', 'Create Alerts', 'Manage Escalations'] },
    { category: 'System Settings', items: ['View Settings', 'Edit Settings', 'Manage Integrations', 'Access Audit Logs'] }
  ];

  const hasPermission = (roleId, permission) => {
    const role = roles[roleId];
    if (!role || !role.permissions) return false;
    const permMap = {
      'Create Users': role.permissions.users?.create,
      'Edit Users': role.permissions.users?.update,
      'Delete Users': role.permissions.users?.delete,
      'Assign Roles': role.permissions.roles?.update,
      'View Own Dashboard': true,
      'View Team Dashboard': roleId === 'director' || roleId === 'super-user',
      'Drill Down': role.permissions.dashboards?.drillDown,
      'Configure Dashboards': role.permissions.dashboards?.configure,
      'Clinic Level': role.permissions.data?.clinicLevel,
      'Service Line Level': role.permissions.data?.serviceLineLevel,
      'Market Level': role.permissions.data?.marketLevel,
      'Enterprise Level': role.permissions.data?.fullAccess,
      'View Tasks': true,
      'Complete Tasks': roleId === 'manager' || roleId === 'director',
      'Assign Tasks': roleId === 'director' || roleId === 'super-user',
      'Create Templates': role.permissions.workflows?.create,
      'View Metrics': true,
      'Configure Thresholds': role.permissions.metrics?.configure,
      'Create Alerts': role.permissions.alerts?.create,
      'Manage Escalations': role.permissions.alerts?.create,
      'View Settings': true,
      'Edit Settings': role.permissions.settings?.update,
      'Manage Integrations': roleId === 'super-user',
      'Access Audit Logs': role.permissions.audit?.read || roleId === 'super-user'
    };
    return permMap[permission] || false;
  };

  const roleCount = Object.keys(roles).length;

  return (
    <div>
      <div className="section-header">
        <h2 className="section-title">Permissions Matrix</h2>
        <p className="section-subtitle">Detailed permissions by role</p>
      </div>

      <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative">
        <div style={{ padding: 0, overflowX: 'auto' }}>
          <table className="nn-table" style={{ minWidth: '900px' }}>
            <thead>
              <tr>
                <th style={{ position: 'sticky', left: 0, background: 'var(--surface-warm)' }}>
                  Permission
                </th>
                {Object.values(roles).map(role => (
                  <th key={role.id} style={{ textAlign: 'center' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                      <FlatIcon text={role.iconText} color={role.color} size={24} />
                      <span style={{ fontSize: '11px' }}>{role.name}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {permissions.map((category, catIdx) => (
                <React.Fragment key={catIdx}>
                  <tr style={{ background: '#F3F4F6' }}>
                    <td colSpan={roleCount + 1} style={{ padding: '8px 16px', fontWeight: '600', fontSize: '13px', color: 'var(--text-dark)' }}>
                      {category.category}
                    </td>
                  </tr>
                  {category.items.map((item, itemIdx) => (
                    <tr key={itemIdx}>
                      <td style={{ position: 'sticky', left: 0, background: 'var(--white)', borderRight: '1px solid var(--color-border)' }}>
                        {item}
                      </td>
                      {Object.keys(roles).map(roleId => (
                        <td key={roleId} style={{ textAlign: 'center' }}>
                          {hasPermission(roleId, item) ? (
                            <span style={{ color: '#10B981', fontSize: '18px' }}>✓</span>
                          ) : (
                            <span style={{ color: '#D1D5DB', fontSize: '18px' }}>−</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Role Detail Modal
function RoleDetailModal({ role, onClose }) {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        maxWidth: '700px',
        width: '90%',
        maxHeight: '80vh',
        overflow: 'auto',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}>
        {/* Modal Header */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #E5E7EB',
          background: role.color,
          color: 'white',
          borderRadius: '12px 12px 0 0'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <FlatIcon text={role.iconText} color="rgba(255,255,255,0.3)" size={56} />
              <div>
                <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600' }}>{role.name}</h2>
                <p style={{ margin: '4px 0 0 0', fontSize: '14px', opacity: 0.9 }}>{role.description}</p>
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onClose}
              style={{ background: 'rgba(255, 255, 255, 0.2)', color: 'white' }}
            >
              ✕
            </Button>
          </div>
        </div>

        {/* Modal Body */}
        <div style={{ padding: '24px' }}>
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px', color: '#1a1a1a' }}>
              Primary Purpose
            </h3>
            <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: '1.6', margin: 0 }}>
              {role.purpose}
            </p>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#1a1a1a' }}>
              Core Capabilities
            </h3>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#6B7280', lineHeight: '2' }}>
              {role.capabilities.map((cap, idx) => (
                <li key={idx}>{cap}</li>
              ))}
            </ul>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#1a1a1a' }}>
              What They Care About
            </h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {role.careLevels.map((care, idx) => (
                <span key={idx} style={{
                  padding: '6px 12px',
                  background: '#F3F4F6',
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: '#4B5563',
                  fontWeight: '500'
                }}>
                  {care}
                </span>
              ))}
            </div>
          </div>

          <div>
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#1a1a1a' }}>
              Key Permissions
            </h3>
            <div style={{
              background: '#F9FAFB',
              padding: '16px',
              borderRadius: '8px',
              fontSize: '13px',
              color: '#6B7280',
              lineHeight: '1.8'
            }}>
              {Object.entries(role.permissions || {}).map(([key, value]) => (
                <div key={key} style={{ marginBottom: '4px' }}>
                  <strong style={{ color: '#1a1a1a', textTransform: 'capitalize' }}>{key}:</strong>{' '}
                  {typeof value === 'object'
                    ? Object.entries(value).filter(([k, v]) => v === true).map(([k]) => k).join(', ')
                    : String(value)
                  }
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #E5E7EB',
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={onClose}
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

// Add User Modal
function AddUserModal({ roles, onClose, onAdd }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'manager',
    market: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onAdd(formData);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        maxWidth: '500px',
        width: '90%',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ padding: '24px', borderBottom: '1px solid #E5E7EB' }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Add New User</h2>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
              Full Name *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #D1D5DB', boxSizing: 'border-box' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
              Email *
            </label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #D1D5DB', boxSizing: 'border-box' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
              Role *
            </label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({...formData, role: e.target.value})}
              className="w-full rounded-md border border-nn-border bg-nn-white px-3 py-2 text-body text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua"
            >
              {Object.values(roles).map(role => (
                <option key={role.id} value={role.id}>
                  [{role.iconText}] {role.name}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
              Market/Region *
            </label>
            <select
              value={formData.market}
              onChange={(e) => setFormData({...formData, market: e.target.value})}
              className="w-full rounded-md border border-nn-border bg-nn-white px-3 py-2 text-body text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua"
            >
              <option value="">Select Market</option>
              <option value="All Markets">All Markets</option>
              <option value="West Region">West Region</option>
              <option value="North Region">North Region</option>
              <option value="South Region">South Region</option>
              <option value="East Region">East Region</option>
            </select>
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <Button
              type="button"
              variant="default"
              size="sm"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="sm"
            >
              Add User
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default RoleManagement;
